 import "./executiveDirectorRegistration.css";
 
 import { useMemo, useReducer, useState } from "react";
 import { useNavigate } from "react-router-dom";
 import { useSnackbar } from "notistack";
 import axios from "axios";
 import * as Yup from "yup";
 
 import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
 import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
 import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
 import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
 import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
 import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
 import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
 import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
 import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
 import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
 import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
 import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
 import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
 import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
 import AddRoundedIcon from "@mui/icons-material/AddRounded";
 import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
 import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
 import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
 import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
 import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
 import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
 
 import IsLoading from "../../IsLoading.jsx";
 import { statesAndLgas } from "../public_directory/public-routs-comps/registrationStatesAndLGA.js";
 
 const steps = [
     {
         id: 1,
         title: "Personal Information",
         description: "Executive identity and contact information",
         icon: <PersonOutlineRoundedIcon />,
     },
     {
         id: 2,
         title: "Executive Territory",
         description: "Region and states under this director",
         icon: <PublicRoundedIcon />,
     },
     {
         id: 3,
         title: "Employment",
         description: "Employment, rank and compensation",
         icon: <WorkOutlineRoundedIcon />,
     },
     {
         id: 4,
         title: "Security",
         description: "Authentication and account security",
         icon: <LockOutlinedIcon />,
     },
     {
         id: 5,
         title: "Review",
         description: "Confirm and create executive account",
         icon: <CheckCircleRoundedIcon />,
     },
 ];
 
 const NigerianStates = Object.keys(statesAndLgas || {});
 
 export default function ExecutiveDirectorRegistration() {
     const navigateTo = useNavigate();
     const { enqueueSnackbar } = useSnackbar();
 
     const [loading, setLoading] = useState(false);
     const [currentStep, setCurrentStep] = useState(1);
     const [errors, setErrors] = useState({});
 
     const [showPassword, setShowPassword] = useState(false);
     const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 
     const [registrationData, setRegistrationData] = useReducer(
         (request, response) => {
             return {
                 ...request,
                 ...response,
             };
         },
         {
             // Personal information
             firstName: "",
             middleName: "",
             surname: "",
             gender: "",
             dateOfBirth: "",
             nationality: "Nigeria",
 
             phone: "",
             alternatePhone: "",
             email: "",
 
             country: "Nigeria",
             state: "",
             city: "",
             localGovernment: "",
             area: "",
             street: "",
             postalCode: "",
 
             emergencyContactName: "",
             emergencyContactRelationship: "",
             emergencyContactPhone: "",
 
             // Organization / territory
             regionId: "",
             regionName: "",
             stateIds: [],
             stateNames: [],
 
             // Employment
             employmentType: "FULL_TIME",
             joinedAt: "",
             rankName: "Executive Director",
             rankLevel: "",
             grade: "",
 
             salaryAmount: "",
             salaryCurrency: "NGN",
             salaryFrequency: "MONTHLY",
 
             allowancesEnabled: true,
 
             // Commission
             commissionEnabled: true,
             clientRegistrationCommission: "3000",
 
             // Authentication
             password: "",
             confirmPassword: "",
 
             // System/account
             accountStatus: "ACTIVE",
             employmentStatus: "ACTIVE",
             authenticationStatus: "ACTIVE",
 
             termsAccepted: false,
         },
     );
 
     const selectedLGAs = useMemo(() => {
         return statesAndLgas?.[registrationData.state] || [];
     }, [registrationData.state]);
 
     // ============================================================
     // VALIDATION
     // ============================================================
 
     const personalValidationSchema = Yup.object().shape({
         firstName: Yup.string().trim().required("First name is required"),
 
         surname: Yup.string().trim().required("Surname is required"),
 
         gender: Yup.string().required("Gender is required"),
 
         email: Yup.string()
             .trim()
             .email("Enter a valid email address")
             .required("Email is required"),
 
         phone: Yup.string().trim().required("Phone number is required"),
 
         nationality: Yup.string().required("Nationality is required"),
 
         state: Yup.string().required("State is required"),
     });
 
     const territoryValidationSchema = Yup.object().shape({
         regionId: Yup.string().trim().required("Region ID is required"),
 
         regionName: Yup.string().trim().required("Region name is required"),
 
         stateNames: Yup.array()
             .min(1, "Assign at least one state to this Executive Director")
             .required("Executive territory is required"),
     });
 
     const employmentValidationSchema = Yup.object().shape({
         employmentType: Yup.string().required("Employment type is required"),
 
         joinedAt: Yup.string().required("Employment start date is required"),
 
         rankName: Yup.string().required("Rank is required"),
 
         salaryAmount: Yup.number()
             .typeError("Salary must be a valid amount")
             .min(0, "Salary cannot be negative")
             .required("Salary amount is required"),
 
         clientRegistrationCommission: Yup.number()
             .typeError("Commission must be a valid amount")
             .min(0, "Commission cannot be negative"),
     });
 
     const securityValidationSchema = Yup.object().shape({
         password: Yup.string()
             .min(8, "Password must contain at least 8 characters")
             .required("Password is required"),
 
         confirmPassword: Yup.string()
             .oneOf([Yup.ref("password")], "Passwords do not match")
             .required("Please confirm the password"),
     });
 
     const validateCurrentStep = async () => {
         try {
             let schema = null;
 
             switch (currentStep) {
                 case 1:
                     schema = personalValidationSchema;
                     break;
 
                 case 2:
                     schema = territoryValidationSchema;
                     break;
 
                 case 3:
                     schema = employmentValidationSchema;
                     break;
 
                 case 4:
                     schema = securityValidationSchema;
                     break;
 
                 default:
                     return true;
             }
 
             await schema.validate(registrationData, {
                 abortEarly: false,
             });
 
             setErrors({});
 
             return true;
         } catch (validationErrors) {
             const formattedErrors = {};
 
             validationErrors.inner?.forEach((error) => {
                 formattedErrors[error.path] = error.message;
             });
 
             setErrors(formattedErrors);
 
             enqueueSnackbar(
                 "Please complete the required information before continuing.",
                 {
                     variant: "warning",
                     autoHideDuration: 3000,
                 },
             );
 
             return false;
         }
     };
 
     const goToNextStep = async () => {
         const isValid = await validateCurrentStep();
 
         if (!isValid) return;
 
         setCurrentStep((previous) => Math.min(previous + 1, steps.length));
 
         window.scrollTo({
             top: 0,
             behavior: "smooth",
         });
     };
 
     const goToPreviousStep = () => {
         setErrors({});
 
         setCurrentStep((previous) => Math.max(previous - 1, 1));
 
         window.scrollTo({
             top: 0,
             behavior: "smooth",
         });
     };
 
     // ============================================================
     // TERRITORY
     // ============================================================
 
     const addTerritoryState = (stateName) => {
         if (!stateName) return;
 
         if (registrationData.stateNames.includes(stateName)) {
             enqueueSnackbar(`${stateName} is already assigned.`, {
                 variant: "info",
                 autoHideDuration: 2000,
             });
 
             return;
         }
 
         setRegistrationData({
             stateNames: [...registrationData.stateNames, stateName],
         });
     };
 
     const removeTerritoryState = (stateName) => {
         setRegistrationData({
             stateNames: registrationData.stateNames.filter(
                 (item) => item !== stateName,
             ),
         });
     };
 
     // ============================================================
     // PAYLOAD
     // ============================================================
 
     const buildExecutiveDirectorPayload = () => {
         const now = new Date();
 
         return {
             staffData: {
                 staffId: "",
 
                 account: {
                     status: registrationData.accountStatus,
                     accessType: "STAFF_ACCESS",
 
                     activated: true,
                     activatedAt: now,
 
                     suspendedAt: null,
                     suspensionReason: null,
                 },
 
                 personalInfo: {
                     firstName: registrationData.firstName.trim(),
                     middleName: registrationData.middleName.trim(),
                     surname: registrationData.surname.trim(),
 
                     gender: registrationData.gender,
                     dateOfBirth: registrationData.dateOfBirth || null,
                     nationality: registrationData.nationality,
 
                     phone: registrationData.phone.trim(),
                     alternatePhone: registrationData.alternatePhone.trim(),
 
                     email: registrationData.email.trim().toLowerCase(),
 
                     photo: "",
 
                     address: {
                         country: registrationData.country,
                         state: registrationData.state,
                         city: registrationData.city.trim(),
                         localGovernment: registrationData.localGovernment,
                         area: registrationData.area.trim(),
                         street: registrationData.street.trim(),
                         postalCode: registrationData.postalCode.trim(),
                     },
 
                     emergencyContact: {
                         name: registrationData.emergencyContactName.trim(),
 
                         relationship:
                             registrationData.emergencyContactRelationship.trim(),
 
                         phone: registrationData.emergencyContactPhone.trim(),
 
                         alternatePhone: "",
                     },
                 },
 
                 role: {
                     roleId: "ROLE-ED",
                     name: "ED",
                     title: "Executive Director",
                     level: "EXECUTIVE",
                     permissions: [],
                 },
 
                 referral: {
                     code: "",
                     active: true,
 
                     statistics: {
                         totalReferrals: 0,
                         activeClients: 0,
                         inactiveClients: 0,
                         pendingClients: 0,
                     },
 
                     createdAt: now,
                     updatedAt: now,
                 },
 
                 organization: {
                     regionId: registrationData.regionId || null,
 
                     stateId: null,
 
                     reportsTo: {
                         staffId: null,
                         role: null,
                     },
 
                     jurisdiction: {
                         regionId: registrationData.regionId || null,
 
                         stateIds: registrationData.stateIds || [],
 
                         stateNames: registrationData.stateNames,
                     },
                 },
 
                 employment: {
                     status: registrationData.employmentStatus,
 
                     employmentType: registrationData.employmentType,
 
                     joinedAt: registrationData.joinedAt || now,
 
                     confirmedAt: null,
 
                     terminatedAt: null,
 
                     terminationReason: null,
 
                     rank: {
                         name: registrationData.rankName,
                         level: registrationData.rankLevel,
                         grade: registrationData.grade,
                     },
 
                     salary: {
                         amount: Number(registrationData.salaryAmount) || 0,
 
                         currency: registrationData.salaryCurrency,
 
                         frequency: registrationData.salaryFrequency,
                     },
 
                     allowances: {
                         enabled: registrationData.allowancesEnabled,
 
                         items: [],
                     },
 
                     commission: {
                         enabled: registrationData.commissionEnabled,
 
                         clientRegistration: {
                             amount:
                                 Number(
                                     registrationData.clientRegistrationCommission,
                                 ) || 0,
 
                             currency: "NGN",
                         },
                     },
                 },
 
                 territory: {
                     regionId: registrationData.regionId || null,
 
                     regionName: registrationData.regionName,
 
                     states: registrationData.stateNames.map(
                         (stateName, index) => ({
                             stateId: registrationData.stateIds?.[index] || null,
 
                             stateName,
 
                             status: "ACTIVE",
 
                             assignedAt: now,
                         }),
                     ),
 
                     totalStates: registrationData.stateNames.length,
 
                     assignedAt: now,
 
                     lastUpdatedAt: now,
                 },
 
                 management: {
                     sdds: {
                         total: 0,
                         active: 0,
                         inactive: 0,
                     },
 
                     fdcs: {
                         total: 0,
                         active: 0,
                         inactive: 0,
                     },
 
                     clients: {
                         total: 0,
                         active: 0,
                         inactive: 0,
                         pending: 0,
                     },
                 },
 
                 performance: {
                     currentPeriod: {
                         target: 0,
                         achieved: 0,
                         percentage: 0,
                         clientsRegistered: 0,
                         activeClients: 0,
                     },
 
                     lifetime: {
                         clientsRegistered: 0,
                         targetsAssigned: 0,
                         targetsCompleted: 0,
                     },
 
                     lastCalculatedAt: null,
                 },
 
                 targets: {
                     currentTargetId: null,
 
                     assigned: 0,
                     completed: 0,
                     pending: 0,
 
                     lastAssignedAt: null,
                 },
 
                 wallet: {
                     balance: 0,
                     pendingBalance: 0,
                     availableBalance: 0,
 
                     totalEarned: 0,
                     totalWithdrawn: 0,
 
                     currency: "NGN",
 
                     lastTransactionAt: null,
                 },
 
                 payroll: {
                     salaryBalance: 0,
                     allowanceBalance: 0,
                     commissionBalance: 0,
 
                     deductions: 0,
                     taxes: 0,
 
                     lastPaymentAt: null,
                     nextPaymentAt: null,
 
                     currency: "NGN",
                 },
 
                 training: {
                     assigned: 0,
                     completed: 0,
                     pending: 0,
 
                     testsTaken: 0,
                     testsPassed: 0,
                     testsFailed: 0,
 
                     lastTrainingAt: null,
                 },
 
                 disciplinary: {
                     queries: {
                         total: 0,
                         open: 0,
                         resolved: 0,
                     },
 
                     warnings: {
                         total: 0,
                         active: 0,
                     },
 
                     suspended: false,
 
                     lastQueryAt: null,
                     lastWarningAt: null,
                 },
 
                 documents: {
                     profileDocuments: [],
 
                     identification: {
                         type: "",
                         number: "",
                         documentUrl: "",
                         verified: false,
                         verifiedAt: null,
                     },
 
                     employmentDocuments: [],
                 },
 
                 auth: {
                     credentials: {
                         // Backend must hash this before inserting.
                         hashedPassword: registrationData.password,
 
                         passwordUpdatedAt: null,
                         lastPasswordResetAt: null,
                     },
 
                     authentication: {
                         method: "EMAIL_PHONE_AND_PASSWORD",
 
                         status: registrationData.authenticationStatus,
 
                         lastLoginAt: null,
 
                         failedLoginAttempts: 0,
 
                         locked: false,
                         lockedAt: null,
                         lockReason: null,
                     },
                 },
 
                 security: {
                     twoFactorEnabled: false,
 
                     loginAttempts: 0,
 
                     accountLocked: false,
 
                     lastPasswordChange: null,
 
                     lastSuccessfulLogin: null,
                     lastFailedLogin: null,
 
                     trustedDevices: [],
                 },
 
                 activity: {
                     lastLogin: null,
                     lastSeen: null,
                     lastActionAt: null,
 
                     lastDevice: null,
                     lastIpAddress: null,
                 },
 
                 audit: {
                     createdBy: "SYSTEM",
 
                     lastModifiedBy: "",
 
                     approvedBy: null,
                     approvedAt: null,
                 },
 
                 createdAt: now,
                 updatedAt: now,
             },
         };
     };
 
     // ============================================================
     // API
     // ============================================================
 
     const checkExecutiveDirectorExistence = async () => {
         try {
             setLoading(true);
 
             const response = await axios.get(
                 `${
                     process.env.REACT_APP_SERVER_SCRIPT_HOST
                 }/universe-staff/register/checkExist/${encodeURIComponent(
                     registrationData.email.trim(),
                 )}`,
             );
 
             if (response.data?.status === 201) {
                 await apiPostExecutiveDirector();
 
                 return;
             }
 
             enqueueSnackbar(
                 response.data?.message ||
                     "A staff account already exists with this information.",
                 {
                     variant: "error",
                     autoHideDuration: 4000,
                 },
             );
         } catch (error) {
             console.error("Executive Director existence check error:", error?.code || "REQUEST_FAILED");
 
             let message = "Unable to verify this staff account.";
 
             if (error.code === "ERR_NETWORK") {
                 message =
                     "No internet connection. Please check your network and try again.";
             } else if (error.response?.data?.message) {
                 message = error.response.data.message;
             } else if (error.message) {
                 message = error.message;
             }
 
             enqueueSnackbar(message, {
                 variant: "error",
                 autoHideDuration: 4000,
             });
         } finally {
             setLoading(false);
         }
     };
 
     const apiPostExecutiveDirector = async () => {
         try {
             setLoading(true);
 
             const payload = buildExecutiveDirectorPayload();
 
             const { data } = await axios.post(
                 `${process.env.REACT_APP_SERVER_SCRIPT_HOST}/universe-staff/register/executive-director`,
                 payload,
                 {
                     timeout: 30000,
                 },
             );
 
             switch (data?.status) {
                 case 201:
                     enqueueSnackbar(
                         data?.message ||
                             "Executive Director registered successfully.",
                         {
                             variant: "success",
                             autoHideDuration: 3500,
                         },
                     );
 
                     navigateTo("/administration/executive/director-teritory");
 
                     break;
 
                 case 400:
                 case 401:
                 case 403:
                 case 404:
                 case 408:
                 case 409:
                 case 422:
                 case 500:
                     enqueueSnackbar(
                         data?.message ||
                             "Executive Director registration failed.",
                         {
                             variant: "error",
                             autoHideDuration: 4000,
                         },
                     );
 
                     break;
 
                 default:
                     enqueueSnackbar(data?.message || "Something went wrong.", {
                         variant: "warning",
                         autoHideDuration: 4000,
                     });
             }
         } catch (error) {
             console.error("Executive Director Registration Error:", error?.code || "REQUEST_FAILED");
 
             let message = "Something went wrong. Please try again.";
 
             if (error.code === "ERR_NETWORK") {
                 message =
                     "No internet connection. Please check your network and try again.";
             } else if (error.code === "ECONNABORTED") {
                 message = "The request timed out. Please try again.";
             } else if (error.response) {
                 message =
                     error.response.data?.message ||
                     `Server responded with status ${error.response.status}.`;
             } else if (error.request) {
                 message =
                     "Server is currently unavailable. Please try again later.";
             } else {
                 message = error.message;
             }
 
             enqueueSnackbar(message, {
                 variant: "error",
                 autoHideDuration: 4000,
             });
         } finally {
             setLoading(false);
         }
     };
 
     const executeRegistration = async () => {
         if (!registrationData.termsAccepted) {
             enqueueSnackbar(
                 "Please confirm the Executive Director information before registration.",
                 {
                     variant: "warning",
                     autoHideDuration: 3000,
                 },
             );
 
             return;
         }
 
         await checkExecutiveDirectorExistence();
     };
 
     return (
         <main className="uiedr">
             {loading && <IsLoading />}
 
             {/* ========================================================
           TOP HEADER
       ======================================================== */}
 
             <header className="uiedrHeader">
                 <div className="uiedrHeaderInner">
                     <div className="uiedrBrand">
                         <span className="uiedrBrandIcon">
                             <ApartmentRoundedIcon />
                         </span>
 
                         <div>
                             <strong>Universe Inventory</strong>
                             <span>Workforce Administration</span>
                         </div>
                     </div>
 
                     <div className="uiedrHeaderStatus">
                         <ShieldOutlinedIcon />
 
                         <div>
                             <strong>Administration workspace</strong>
                             <span>Executive Director onboarding</span>
                         </div>
                     </div>
                 </div>
             </header>
 
             <section className="uiedrLayout">
                 {/* ======================================================
             TOP STEP NAVIGATION
         ====================================================== */}
 
                 <section className="uiedrSetupHeader" aria-label="Executive account setup">
                     <div className="uiedrIntro">
                         <div>
                             <span className="uiedrEyebrow">WORKFORCE ADMINISTRATION</span>
                             <h1>Create an executive account</h1>
                             <p>Add your Executive Director to the Universe workforce. Complete the profile, territory and access settings below.</p>
                         </div>
                         <span className="uiedrRoleBadge"><VerifiedUserOutlinedIcon /> Executive account</span>
                     </div>
                     <div className="uiedrProgressSummary">
                         <span>Account setup <strong>{currentStep} / {steps.length}</strong></span>
                         <span>{steps[currentStep - 1].title}</span>
                     </div>
                     <nav className="uiedrSteps" aria-label="Registration steps">
                         {steps.map((step) => {
                             const active = currentStep === step.id;
                             const completed = currentStep > step.id;
 
                             return (
                                 <button
                                     type="button"
                                     key={step.id}
                                     aria-current={active ? "step" : undefined}
                                     disabled={loading || step.id > currentStep}
                                     className={`uiedrStep ${
                                         active ? "active" : ""
                                     } ${completed ? "completed" : ""}`}
                                     onClick={() => {
                                         if (step.id < currentStep) {
                                             setCurrentStep(step.id);
                                             setErrors({});
                                         }
                                     }}
                                 >
                                     <span className="uiedrStepIcon">
                                         {completed ? (
                                             <CheckCircleRoundedIcon />
                                         ) : (
                                             <span>{String(step.id).padStart(2, "0")}</span>
                                         )}
                                     </span>
 
                                     <span className="uiedrStepText">
                                         <small>{completed ? "Completed" : active ? "In progress" : `Step ${step.id}`}</small>
                                         <strong>{step.title}</strong>
                                         <span>{step.description}</span>
                                     </span>
                                 </button>
                             );
                         })}
                     </nav>
 
                     <div className="uiedrProgressTrack" role="progressbar" aria-label="Current registration step" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={currentStep}>
                         <span style={{ width: `${(currentStep / steps.length) * 100}%` }} />
                     </div>
                 </section>
 
                 {/* ======================================================
             FORM CONTENT
         ====================================================== */}
 
                 <section className="uiedrContent">
                     <div className="uiedrContentInner">
                         <div className="uiedrMobileProgress">
                             <span>
                                 Step {currentStep} of {steps.length}
                             </span>
 
                             <div>
                                 <i
                                     style={{
                                         width: `${
                                             (currentStep / steps.length) * 100
                                         }%`,
                                     }}
                                 />
                             </div>
                         </div>
 
                         {/* ==================================================
                 STEP 1
             ================================================== */}
 
                         {currentStep === 1 && (
                             <>
                                 <FormHeading
                                     icon={<PersonOutlineRoundedIcon />}
                                     eyebrow="Executive profile"
                                     title="Personal Information"
                                     description="Enter the Executive Director's identity, primary contact information and residential details."
                                 />
 
                                 <FormSection
                                     title="Basic information"
                                     description="Official identity information for the executive workforce profile."
                                 >
                                     <div className="uiedrGrid three">
                                         <Field
                                             label="First name"
                                             required
                                             error={errors.firstName}
                                         >
                                             <input
                                                 value={
                                                     registrationData.firstName
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         firstName:
                                                             event.target.value,
                                                     })
                                                 }
                                                 placeholder="First name"
                                             />
                                         </Field>
 
                                         <Field label="Middle name">
                                             <input
                                                 value={
                                                     registrationData.middleName
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         middleName:
                                                             event.target.value,
                                                     })
                                                 }
                                                 placeholder="Middle name"
                                             />
                                         </Field>
 
                                         <Field
                                             label="Surname"
                                             required
                                             error={errors.surname}
                                         >
                                             <input
                                                 value={registrationData.surname}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         surname:
                                                             event.target.value,
                                                     })
                                                 }
                                                 placeholder="Surname"
                                             />
                                         </Field>
                                     </div>
 
                                     <div className="uiedrGrid three">
                                         <Field
                                             label="Gender"
                                             required
                                             error={errors.gender}
                                         >
                                             <select
                                                 value={registrationData.gender}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         gender: event.target
                                                             .value,
                                                     })
                                                 }
                                             >
                                                 <option value="">
                                                     Select gender
                                                 </option>
 
                                                 <option value="MALE">
                                                     Male
                                                 </option>
 
                                                 <option value="FEMALE">
                                                     Female
                                                 </option>
                                             </select>
                                         </Field>
 
                                         <Field label="Date of birth">
                                             <input
                                                 type="date"
                                                 value={
                                                     registrationData.dateOfBirth
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         dateOfBirth:
                                                             event.target.value,
                                                     })
                                                 }
                                             />
                                         </Field>
 
                                         <Field
                                             label="Nationality"
                                             required
                                             error={errors.nationality}
                                         >
                                             <input
                                                 value={
                                                     registrationData.nationality
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         nationality:
                                                             event.target.value,
                                                     })
                                                 }
                                             />
                                         </Field>
                                     </div>
                                 </FormSection>
 
                                 <FormSection
                                     title="Contact information"
                                     description="Primary communication channels attached to this staff account."
                                 >
                                     <div className="uiedrGrid two">
                                         <Field
                                             label="Email address"
                                             required
                                             error={errors.email}
                                             icon={<MailOutlineRoundedIcon />}
                                         >
                                             <input
                                                 type="email"
                                                 value={registrationData.email}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         email: event.target
                                                             .value,
                                                     })
                                                 }
                                                 placeholder="executive@universeinventory.com"
                                             />
                                         </Field>
 
                                         <Field
                                             label="Phone number"
                                             required
                                             error={errors.phone}
                                             icon={<PhoneOutlinedIcon />}
                                         >
                                             <input
                                                 value={registrationData.phone}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         phone: event.target
                                                             .value,
                                                     })
                                                 }
                                                 placeholder="+234"
                                             />
                                         </Field>
 
                                         <Field label="Alternate phone">
                                             <input
                                                 value={
                                                     registrationData.alternatePhone
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         alternatePhone:
                                                             event.target.value,
                                                     })
                                                 }
                                                 placeholder="Optional"
                                             />
                                         </Field>
                                     </div>
                                 </FormSection>
 
                                 <FormSection
                                     title="Residential address"
                                     description="Primary address information associated with the Executive Director."
                                 >
                                     <div className="uiedrGrid three">
                                         <Field label="Country">
                                             <input
                                                 value={registrationData.country}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         country:
                                                             event.target.value,
                                                     })
                                                 }
                                             />
                                         </Field>
 
                                         <Field
                                             label="State"
                                             required
                                             error={errors.state}
                                         >
                                             <select
                                                 value={registrationData.state}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         state: event.target
                                                             .value,
                                                         localGovernment: "",
                                                     })
                                                 }
                                             >
                                                 <option value="">
                                                     Select state
                                                 </option>
 
                                                 {NigerianStates.map((state) => (
                                                     <option
                                                         value={state}
                                                         key={state}
                                                     >
                                                         {state}
                                                     </option>
                                                 ))}
                                             </select>
                                         </Field>
 
                                         <Field label="Local Government">
                                             <select
                                                 value={
                                                     registrationData.localGovernment
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         localGovernment:
                                                             event.target.value,
                                                     })
                                                 }
                                                 disabled={
                                                     !registrationData.state
                                                 }
                                             >
                                                 <option value="">
                                                     Select LGA
                                                 </option>
 
                                                 {selectedLGAs.map((lga) => (
                                                     <option
                                                         value={lga}
                                                         key={lga}
                                                     >
                                                         {lga}
                                                     </option>
                                                 ))}
                                             </select>
                                         </Field>
 
                                         <Field label="City">
                                             <input
                                                 value={registrationData.city}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         city: event.target
                                                             .value,
                                                     })
                                                 }
                                             />
                                         </Field>
 
                                         <Field label="Area">
                                             <input
                                                 value={registrationData.area}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         area: event.target
                                                             .value,
                                                     })
                                                 }
                                             />
                                         </Field>
 
                                         <Field label="Street">
                                             <input
                                                 value={registrationData.street}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         street: event.target
                                                             .value,
                                                     })
                                                 }
                                             />
                                         </Field>
                                     </div>
                                 </FormSection>
 
                                 <FormSection
                                     title="Emergency contact"
                                     description="Optional emergency contact for the staff employment record."
                                 >
                                     <div className="uiedrGrid three">
                                         <Field label="Full name">
                                             <input
                                                 value={
                                                     registrationData.emergencyContactName
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         emergencyContactName:
                                                             event.target.value,
                                                     })
                                                 }
                                             />
                                         </Field>
 
                                         <Field label="Relationship">
                                             <input
                                                 value={
                                                     registrationData.emergencyContactRelationship
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         emergencyContactRelationship:
                                                             event.target.value,
                                                     })
                                                 }
                                             />
                                         </Field>
 
                                         <Field label="Phone">
                                             <input
                                                 value={
                                                     registrationData.emergencyContactPhone
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         emergencyContactPhone:
                                                             event.target.value,
                                                     })
                                                 }
                                             />
                                         </Field>
                                     </div>
                                 </FormSection>
                             </>
                         )}
 
                         {/* ==================================================
                 STEP 2
             ================================================== */}
 
                         {currentStep === 2 && (
                             <>
                                 <FormHeading
                                     icon={<PublicRoundedIcon />}
                                     eyebrow="Organizational jurisdiction"
                                     title="Executive Territory"
                                     description="Define the regional jurisdiction and states that fall under this Executive Director."
                                 />
 
                                 <div className="uiedrRoleBanner">
                                     <span>
                                         <BadgeOutlinedIcon />
                                     </span>
 
                                     <div>
                                         <small>Assigned role</small>
                                         <strong>Executive Director</strong>
                                         <p>
                                             ED · Executive Level · Universe
                                             Workforce
                                         </p>
                                     </div>
 
                                     <span className="uiedrRoleBadge">ED</span>
                                 </div>
 
                                 <FormSection
                                     title="Region assignment"
                                     description="The ED sits above State Development Directors and manages the assigned regional jurisdiction."
                                 >
                                     <div className="uiedrGrid two">
                                         <Field
                                             label="Region ID"
                                             required
                                             error={errors.regionId}
                                         >
                                             <input
                                                 value={
                                                     registrationData.regionId
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         regionId:
                                                             event.target.value,
                                                     })
                                                 }
                                                 placeholder="Region identifier"
                                             />
                                         </Field>
 
                                         <Field
                                             label="Region name"
                                             required
                                             error={errors.regionName}
                                         >
                                             <input
                                                 value={
                                                     registrationData.regionName
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         regionName:
                                                             event.target.value,
                                                     })
                                                 }
                                                 placeholder="e.g. North West"
                                             />
                                         </Field>
                                     </div>
                                 </FormSection>
 
                                 <FormSection
                                     title="States under this Executive Director"
                                     description="Add every state that belongs to this ED's regional jurisdiction."
                                 >
                                     <div className="uiedrStatePicker">
                                         <select
                                             defaultValue=""
                                             onChange={(event) => {
                                                 addTerritoryState(
                                                     event.target.value,
                                                 );
 
                                                 event.target.value = "";
                                             }}
                                         >
                                             <option value="">
                                                 Select state to assign
                                             </option>
 
                                             {NigerianStates.map((state) => (
                                                 <option
                                                     value={state}
                                                     key={state}
                                                 >
                                                     {state}
                                                 </option>
                                             ))}
                                         </select>
 
                                         <button type="button">
                                             <AddRoundedIcon />
                                             Add state
                                         </button>
                                     </div>
 
                                     {errors.stateNames && (
                                         <p className="uiedrError">
                                             {errors.stateNames}
                                         </p>
                                     )}
 
                                     <div className="uiedrAssignedStates">
                                         {registrationData.stateNames.length >
                                         0 ? (
                                             registrationData.stateNames.map(
                                                 (state) => (
                                                     <div
                                                         className="uiedrState"
                                                         key={state}
                                                     >
                                                         <span>
                                                             <LocationOnOutlinedIcon />
                                                         </span>
 
                                                         <div>
                                                             <strong>
                                                                 {state}
                                                             </strong>
                                                             <small>
                                                                 Active
                                                                 jurisdiction
                                                             </small>
                                                         </div>
 
                                                         <button
                                                             type="button"
                                                             onClick={() =>
                                                                 removeTerritoryState(
                                                                     state,
                                                                 )
                                                             }
                                                         >
                                                             <CloseRoundedIcon />
                                                         </button>
                                                     </div>
                                                 ),
                                             )
                                         ) : (
                                             <div className="uiedrEmptyState">
                                                 <PublicRoundedIcon />
 
                                                 <strong>
                                                     No states assigned yet
                                                 </strong>
 
                                                 <p>
                                                     Select the states that will
                                                     be supervised by this
                                                     Executive Director.
                                                 </p>
                                             </div>
                                         )}
                                     </div>
                                 </FormSection>
 
                                 <div className="uiedrHierarchy">
                                     <div className="uiedrHierarchyHeader">
                                         <GroupsOutlinedIcon />
 
                                         <div>
                                             <strong>
                                                 Organizational hierarchy
                                             </strong>
 
                                             <p>
                                                 This structure will determine
                                                 the workforce reporting chain.
                                             </p>
                                         </div>
                                     </div>
 
                                     <div className="uiedrHierarchyFlow">
                                         <span className="primary">
                                             <strong>ED</strong>
                                             Executive Director
                                         </span>
 
                                         <i />
 
                                         <span>
                                             <strong>SDD</strong>
                                             State Directors
                                         </span>
 
                                         <i />
 
                                         <span>
                                             <strong>FDC</strong>
                                             Field Consultants
                                         </span>
                                     </div>
                                 </div>
                             </>
                         )}
 
                         {/* ==================================================
                 STEP 3
             ================================================== */}
 
                         {currentStep === 3 && (
                             <>
                                 <FormHeading
                                     icon={<WorkOutlineRoundedIcon />}
                                     eyebrow="Employment profile"
                                     title="Employment & Compensation"
                                     description="Configure employment status, executive rank, salary and client acquisition commission."
                                 />
 
                                 <FormSection
                                     title="Employment details"
                                     description="Core employment information for the Executive Director."
                                 >
                                     <div className="uiedrGrid three">
                                         <Field
                                             label="Employment type"
                                             required
                                             error={errors.employmentType}
                                         >
                                             <select
                                                 value={
                                                     registrationData.employmentType
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         employmentType:
                                                             event.target.value,
                                                     })
                                                 }
                                             >
                                                 <option value="FULL_TIME">
                                                     Full Time
                                                 </option>
 
                                                 <option value="PART_TIME">
                                                     Part Time
                                                 </option>
 
                                                 <option value="CONTRACT">
                                                     Contract
                                                 </option>
                                             </select>
                                         </Field>
 
                                         <Field
                                             label="Joined date"
                                             required
                                             error={errors.joinedAt}
                                         >
                                             <input
                                                 type="date"
                                                 value={
                                                     registrationData.joinedAt
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         joinedAt:
                                                             event.target.value,
                                                     })
                                                 }
                                             />
                                         </Field>
 
                                         <Field label="Employment status">
                                             <select
                                                 value={
                                                     registrationData.employmentStatus
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         employmentStatus:
                                                             event.target.value,
                                                     })
                                                 }
                                             >
                                                 <option value="ACTIVE">
                                                     Active
                                                 </option>
 
                                                 <option value="PENDING">
                                                     Pending
                                                 </option>
                                             </select>
                                         </Field>
                                     </div>
                                 </FormSection>
 
                                 <FormSection
                                     title="Executive rank"
                                     description="Organizational level and grade attached to this executive appointment."
                                 >
                                     <div className="uiedrGrid three">
                                         <Field label="Rank">
                                             <input
                                                 value={
                                                     registrationData.rankName
                                                 }
                                                 disabled
                                             />
                                         </Field>
 
                                         <Field label="Level">
                                             <input
                                                 value={
                                                     registrationData.rankLevel
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         rankLevel:
                                                             event.target.value,
                                                     })
                                                 }
                                                 placeholder="Executive level"
                                             />
                                         </Field>
 
                                         <Field label="Grade">
                                             <input
                                                 value={registrationData.grade}
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         grade: event.target
                                                             .value,
                                                     })
                                                 }
                                                 placeholder="Grade"
                                             />
                                         </Field>
                                     </div>
                                 </FormSection>
 
                                 <FormSection
                                     title="Salary"
                                     description="Configure the base salary attached to this employment profile."
                                 >
                                     <div className="uiedrGrid three">
                                         <Field
                                             label="Salary amount"
                                             required
                                             error={errors.salaryAmount}
                                             icon={
                                                 <AccountBalanceWalletOutlinedIcon />
                                             }
                                         >
                                             <input
                                                 type="number"
                                                 min="0"
                                                 value={
                                                     registrationData.salaryAmount
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         salaryAmount:
                                                             event.target.value,
                                                     })
                                                 }
                                                 placeholder="0.00"
                                             />
                                         </Field>
 
                                         <Field label="Currency">
                                             <select
                                                 value={
                                                     registrationData.salaryCurrency
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         salaryCurrency:
                                                             event.target.value,
                                                     })
                                                 }
                                             >
                                                 <option value="NGN">
                                                     NGN — Nigerian Naira
                                                 </option>
                                             </select>
                                         </Field>
 
                                         <Field label="Payment frequency">
                                             <select
                                                 value={
                                                     registrationData.salaryFrequency
                                                 }
                                                 onChange={(event) =>
                                                     setRegistrationData({
                                                         salaryFrequency:
                                                             event.target.value,
                                                     })
                                                 }
                                             >
                                                 <option value="MONTHLY">
                                                     Monthly
                                                 </option>
 
                                                 <option value="WEEKLY">
                                                     Weekly
                                                 </option>
                                             </select>
                                         </Field>
                                     </div>
                                 </FormSection>
 
                                 <FormSection
                                     title="Client registration commission"
                                     description="Commission configuration applied when this executive is credited with a client acquisition."
                                 >
                                     <div className="uiedrCommission">
                                         <div className="uiedrCommissionSwitch">
                                             <div>
                                                 <strong>
                                                     Registration commission
                                                 </strong>
 
                                                 <p>
                                                     Enable client registration
                                                     commission for this account.
                                                 </p>
                                             </div>
 
                                             <button
                                                 type="button"
                                                 className={
                                                     registrationData.commissionEnabled
                                                         ? "active"
                                                         : ""
                                                 }
                                                 onClick={() =>
                                                     setRegistrationData({
                                                         commissionEnabled:
                                                             !registrationData.commissionEnabled,
                                                     })
                                                 }
                                             >
                                                 <span />
                                             </button>
                                         </div>
 
                                         {registrationData.commissionEnabled && (
                                             <div className="uiedrGrid two">
                                                 <Field
                                                     label="Amount per registered client"
                                                     error={
                                                         errors.clientRegistrationCommission
                                                     }
                                                 >
                                                     <div className="uiedrMoneyInput">
                                                         <span>₦</span>
 
                                                         <input
                                                             type="number"
                                                             min="0"
                                                             value={
                                                                 registrationData.clientRegistrationCommission
                                                             }
                                                             onChange={(event) =>
                                                                 setRegistrationData(
                                                                     {
                                                                         clientRegistrationCommission:
                                                                             event
                                                                                 .target
                                                                                 .value,
                                                                     },
                                                                 )
                                                             }
                                                         />
                                                     </div>
                                                 </Field>
 
                                                 <div className="uiedrCompensationNote">
                                                     <strong>
                                                         Commission rule
                                                     </strong>
 
                                                     <p>
                                                         This value initializes
                                                         the executive's
                                                         client-registration
                                                         commission
                                                         configuration.
                                                     </p>
                                                 </div>
                                             </div>
                                         )}
                                     </div>
                                 </FormSection>
                             </>
                         )}
 
                         {/* ==================================================
                 STEP 4
             ================================================== */}
 
                         {currentStep === 4 && (
                             <>
                                 <FormHeading
                                     icon={<LockOutlinedIcon />}
                                     eyebrow="Account protection"
                                     title="Security & Authentication"
                                     description="Create the initial credentials used by this Executive Director to access the Universe workforce portal."
                                 />
 
                                 <div className="uiedrSecurityBanner">
                                     <ShieldOutlinedIcon />
 
                                     <div>
                                         <strong>Universe Staff Access</strong>
 
                                         <p>
                                             This account will authenticate
                                             through the central Universe Staff
                                             login and automatically open the
                                             Executive Director workspace.
                                         </p>
                                     </div>
                                 </div>
 
                                 <FormSection
                                     title="Login identity"
                                     description="The Executive Director can sign in using the registered staff identity."
                                 >
                                     <div className="uiedrIdentityPreview">
                                         <div>
                                             <span>
                                                 <MailOutlineRoundedIcon />
                                             </span>
 
                                             <div>
                                                 <small>Email</small>
 
                                                 <strong>
                                                     {registrationData.email ||
                                                         "Not provided"}
                                                 </strong>
                                             </div>
                                         </div>
 
                                         <div>
                                             <span>
                                                 <PhoneOutlinedIcon />
                                             </span>
 
                                             <div>
                                                 <small>Phone</small>
 
                                                 <strong>
                                                     {registrationData.phone ||
                                                         "Not provided"}
                                                 </strong>
                                             </div>
                                         </div>
 
                                         <div>
                                             <span>
                                                 <BadgeOutlinedIcon />
                                             </span>
 
                                             <div>
                                                 <small>Staff ID</small>
 
                                                 <strong>
                                                     Generated by server
                                                 </strong>
                                             </div>
                                         </div>
                                     </div>
                                 </FormSection>
 
                                 <FormSection
                                     title="Initial password"
                                     description="Create the temporary/initial password for this staff account."
                                 >
                                     <div className="uiedrGrid two">
                                         <Field
                                             label="Password"
                                             required
                                             error={errors.password}
                                         >
                                             <div className="uiedrPassword">
                                                 <LockOutlinedIcon />
 
                                                 <input
                                                     type={
                                                         showPassword
                                                             ? "text"
                                                             : "password"
                                                     }
                                                     value={
                                                         registrationData.password
                                                     }
                                                     onChange={(event) =>
                                                         setRegistrationData({
                                                             password:
                                                                 event.target
                                                                     .value,
                                                         })
                                                     }
                                                     placeholder="Minimum 8 characters"
                                                 />
 
                                                 <button
                                                     type="button"
                                                     onClick={() =>
                                                         setShowPassword(
                                                             (previous) =>
                                                                 !previous,
                                                         )
                                                     }
                                                 >
                                                     {showPassword ? (
                                                         <VisibilityOffOutlinedIcon />
                                                     ) : (
                                                         <VisibilityOutlinedIcon />
                                                     )}
                                                 </button>
                                             </div>
                                         </Field>
 
                                         <Field
                                             label="Confirm password"
                                             required
                                             error={errors.confirmPassword}
                                         >
                                             <div className="uiedrPassword">
                                                 <LockOutlinedIcon />
 
                                                 <input
                                                     type={
                                                         showConfirmPassword
                                                             ? "text"
                                                             : "password"
                                                     }
                                                     value={
                                                         registrationData.confirmPassword
                                                     }
                                                     onChange={(event) =>
                                                         setRegistrationData({
                                                             confirmPassword:
                                                                 event.target
                                                                     .value,
                                                         })
                                                     }
                                                     placeholder="Repeat password"
                                                 />
 
                                                 <button
                                                     type="button"
                                                     onClick={() =>
                                                         setShowConfirmPassword(
                                                             (previous) =>
                                                                 !previous,
                                                         )
                                                     }
                                                 >
                                                     {showConfirmPassword ? (
                                                         <VisibilityOffOutlinedIcon />
                                                     ) : (
                                                         <VisibilityOutlinedIcon />
                                                     )}
                                                 </button>
                                             </div>
                                         </Field>
                                     </div>
                                 </FormSection>
 
                                 <FormSection
                                     title="Account status"
                                     description="Initial access state for this workforce account."
                                 >
                                     <div className="uiedrStatusGrid">
                                         <StatusOption
                                             title="Active"
                                             description="Account can sign in immediately after registration."
                                             active={
                                                 registrationData.accountStatus ===
                                                 "ACTIVE"
                                             }
                                             onClick={() =>
                                                 setRegistrationData({
                                                     accountStatus: "ACTIVE",
                                                     authenticationStatus:
                                                         "ACTIVE",
                                                 })
                                             }
                                         />
 
                                         <StatusOption
                                             title="Pending"
                                             description="Create the profile but prevent immediate workforce access."
                                             active={
                                                 registrationData.accountStatus ===
                                                 "PENDING"
                                             }
                                             onClick={() =>
                                                 setRegistrationData({
                                                     accountStatus: "PENDING",
                                                     authenticationStatus:
                                                         "PENDING",
                                                 })
                                             }
                                         />
                                     </div>
                                 </FormSection>
                             </>
                         )}
 
                         {/* ==================================================
                 STEP 5
             ================================================== */}
 
                         {currentStep === 5 && (
                             <>
                                 <FormHeading
                                     icon={<CheckCircleRoundedIcon />}
                                     eyebrow="Final confirmation"
                                     title="Review Executive Account"
                                     description="Review the Executive Director's information before creating the workforce account."
                                 />
 
                                 <div className="uiedrReviewHero">
                                     <div className="uiedrAvatar">
                                         {registrationData.firstName
                                             ?.charAt(0)
                                             ?.toUpperCase() || "E"}
 
                                         {registrationData.surname
                                             ?.charAt(0)
                                             ?.toUpperCase() || "D"}
                                     </div>
 
                                     <div className="uiedrReviewIdentity">
                                         <span>Executive Director</span>
 
                                         <h2>
                                             {registrationData.firstName}{" "}
                                             {registrationData.middleName}{" "}
                                             {registrationData.surname}
                                         </h2>
 
                                         <p>{registrationData.email}</p>
 
                                         <div>
                                             <span>
                                                 <VerifiedUserOutlinedIcon />
                                                 STAFF_ACCESS
                                             </span>
 
                                             <span>ED</span>
 
                                             <span>
                                                 {registrationData.accountStatus}
                                             </span>
                                         </div>
                                     </div>
                                 </div>
 
                                 <div className="uiedrReviewGrid">
                                     <ReviewCard
                                         icon={<PersonOutlineRoundedIcon />}
                                         title="Personal Information"
                                         onEdit={() => setCurrentStep(1)}
                                     >
                                         <ReviewRow
                                             label="Full name"
                                             value={`${registrationData.firstName} ${registrationData.middleName} ${registrationData.surname}`}
                                         />
 
                                         <ReviewRow
                                             label="Gender"
                                             value={registrationData.gender}
                                         />
 
                                         <ReviewRow
                                             label="Phone"
                                             value={registrationData.phone}
                                         />
 
                                         <ReviewRow
                                             label="State"
                                             value={registrationData.state}
                                         />
                                     </ReviewCard>
 
                                     <ReviewCard
                                         icon={<PublicRoundedIcon />}
                                         title="Executive Territory"
                                         onEdit={() => setCurrentStep(2)}
                                     >
                                         <ReviewRow
                                             label="Region"
                                             value={registrationData.regionName}
                                         />
 
                                         <ReviewRow
                                             label="States"
                                             value={
                                                 registrationData.stateNames
                                                     .length
                                             }
                                         />
 
                                         <div className="uiedrReviewStates">
                                             {registrationData.stateNames.map(
                                                 (state) => (
                                                     <span key={state}>
                                                         {state}
                                                     </span>
                                                 ),
                                             )}
                                         </div>
                                     </ReviewCard>
 
                                     <ReviewCard
                                         icon={<WorkOutlineRoundedIcon />}
                                         title="Employment"
                                         onEdit={() => setCurrentStep(3)}
                                     >
                                         <ReviewRow
                                             label="Type"
                                             value={
                                                 registrationData.employmentType
                                             }
                                         />
 
                                         <ReviewRow
                                             label="Joined"
                                             value={registrationData.joinedAt}
                                         />
 
                                         <ReviewRow
                                             label="Salary"
                                             value={`₦${Number(
                                                 registrationData.salaryAmount ||
                                                     0,
                                             ).toLocaleString()}`}
                                         />
 
                                         <ReviewRow
                                             label="Commission"
                                             value={
                                                 registrationData.commissionEnabled
                                                     ? `₦${Number(
                                                           registrationData.clientRegistrationCommission ||
                                                               0,
                                                       ).toLocaleString()}`
                                                     : "Disabled"
                                             }
                                         />
                                     </ReviewCard>
 
                                     <ReviewCard
                                         icon={<ShieldOutlinedIcon />}
                                         title="Access & Security"
                                         onEdit={() => setCurrentStep(4)}
                                     >
                                         <ReviewRow
                                             label="Access type"
                                             value="STAFF_ACCESS"
                                         />
 
                                         <ReviewRow label="Role" value="ED" />
 
                                         <ReviewRow
                                             label="Account"
                                             value={
                                                 registrationData.accountStatus
                                             }
                                         />
 
                                         <ReviewRow
                                             label="Authentication"
                                             value="Email / Phone / Staff ID"
                                         />
                                     </ReviewCard>
                                 </div>
 
                                 <div className="uiedrConfirmation">
                                     <label>
                                         <input
                                             type="checkbox"
                                             checked={
                                                 registrationData.termsAccepted
                                             }
                                             onChange={(event) =>
                                                 setRegistrationData({
                                                     termsAccepted:
                                                         event.target.checked,
                                                 })
                                             }
                                         />
 
                                         <span>
                                             <strong>
                                                 I confirm this executive
                                                 information is correct.
                                             </strong>
 
                                             <small>
                                                 Creating this account will
                                                 register a new Executive
                                                 Director in the Universe
                                                 workforce system.
                                             </small>
                                         </span>
                                     </label>
                                 </div>
                             </>
                         )}
 
                         {/* ==================================================
                 ACTIONS
             ================================================== */}
 
                         <div className="uiedrActions">
                             <button
                                 type="button"
                                 className="uiedrBack"
                                 disabled={currentStep === 1}
                                 onClick={goToPreviousStep}
                             >
                                 <ArrowBackRoundedIcon />
                                 Back
                             </button>
 
                             <div className="uiedrActionRight">
                                 <span>
                                     Step {currentStep} of {steps.length}
                                 </span>
 
                                 {currentStep < steps.length ? (
                                     <button
                                         type="button"
                                         className="uiedrContinue"
                                         onClick={goToNextStep}
                                     >
                                         Continue
                                         <ArrowForwardRoundedIcon />
                                     </button>
                                 ) : (
                                     <button
                                         type="button"
                                         className="uiedrRegister"
                                         onClick={executeRegistration}
                                         disabled={loading}
                                     >
                                         <VerifiedUserOutlinedIcon />
 
                                         {loading
                                             ? "Creating account..."
                                             : "Register Executive Director"}
                                     </button>
                                 )}
                             </div>
                         </div>
                     </div>
                 </section>
             </section>
         </main>
     );
 }
 
 // ============================================================
 // REUSABLE COMPONENTS
 // ============================================================
 
 function FormHeading({ icon, eyebrow, title, description }) {
     return (
         <header className="uiedrFormHeading">
             <span className="uiedrFormHeadingIcon">{icon}</span>
 
             <div>
                 <span className="uiedrFormEyebrow">{eyebrow}</span>
 
                 <h2>{title}</h2>
 
                 <p>{description}</p>
             </div>
         </header>
     );
 }
 
 function FormSection({ title, description, children }) {
     return (
         <section className="uiedrFormSection">
             <header>
                 <h3>{title}</h3>
                 <p>{description}</p>
             </header>
 
             <div className="uiedrSectionBody">{children}</div>
         </section>
     );
 }
 
 function Field({ label, required, error, icon, children }) {
     return (
         <label className={`uiedrField ${error ? "hasError" : ""}`}>
             <span className="uiedrFieldLabel">
                 {label}
 
                 {required && <b>*</b>}
             </span>
 
             <div className={`uiedrInput ${icon ? "hasIcon" : ""}`}>
                 {icon && <span className="uiedrInputIcon">{icon}</span>}
 
                 {children}
             </div>
 
             {error && <span className="uiedrError">{error}</span>}
         </label>
     );
 }
 
 function StatusOption({ title, description, active, onClick }) {
     return (
         <button
             type="button"
             className={`uiedrStatusOption ${active ? "active" : ""}`}
             onClick={onClick}
         >
             <span className="uiedrStatusRadio">
                 <i />
             </span>
 
             <span>
                 <strong>{title}</strong>
                 <small>{description}</small>
             </span>
         </button>
     );
 }
 
 function ReviewCard({ icon, title, onEdit, children }) {
     return (
         <article className="uiedrReviewCard">
             <header>
                 <div>
                     <span>{icon}</span>
                     <strong>{title}</strong>
                 </div>
 
                 <button type="button" onClick={onEdit}>
                     Edit
                 </button>
             </header>
 
             <div className="uiedrReviewBody">{children}</div>
         </article>
     );
 }
 
 function ReviewRow({ label, value }) {
     return (
         <div className="uiedrReviewRow">
             <span>{label}</span>
             <strong>{value || "—"}</strong>
         </div>
     );
 }
 